import type { ConsoleAuditEntry } from '@sentinel/shared';
import { EmptyState, Panel, Table, type Column } from '@sentinel/ui';
import { formatDateTime, Pager, QueryState } from '@sentinel/web-core';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { consoleEndpoints } from '@/api/endpoints';
import { keys } from '@/api/keys';
import { useConsoleCan } from '@/auth/use-console-can';

const K = 'console.audit';
const PAGE_SIZE = 25;

/** The platform audit, newest first: the whole trail with `console.audit:view`, only one's own
 *  actions otherwise. */
export function AuditPage() {
  const { t } = useTranslation();
  const own = !useConsoleCan('console.audit:view');
  const [action, setAction] = useState('');
  const [page, setPage] = useState(1);
  const audit = useQuery({
    queryKey: keys.audit(own, action, page),
    queryFn: () =>
      consoleEndpoints.audit({
        own,
        page,
        pageSize: PAGE_SIZE,
        ...(action === '' ? {} : { action }),
      }),
  });
  const columns: Column<ConsoleAuditEntry>[] = [
    {
      key: 'when',
      header: t(`${K}.columns.when`),
      mono: true,
      render: (e) => formatDateTime(e.occurredAt),
    },
    { key: 'action', header: t(`${K}.columns.action`), mono: true, render: (e) => e.action },
    {
      key: 'org',
      header: t(`${K}.columns.organization`),
      render: (e) => e.organizationName ?? '—',
    },
    { key: 'actor', header: t(`${K}.columns.actor`), render: (e) => e.actor?.displayName ?? '—' },
  ];
  return (
    <Panel flush title={t(own ? `${K}.ownTitle` : `${K}.title`)}>
      <div className="toolbar">
        <span className="search">
          <input
            type="search"
            placeholder={t(`${K}.filter`)}
            aria-label={t(`${K}.filter`)}
            value={action}
            onChange={(e) => {
              setAction(e.target.value.trim());
              setPage(1);
            }}
          />
        </span>
      </div>
      <QueryState query={audit}>
        {(data) =>
          data.items.length === 0 ? (
            <EmptyState title={t(`${K}.empty`)} description={t(`${K}.emptyHint`)} />
          ) : (
            <>
              <Table
                caption={t(`${K}.title`)}
                columns={columns}
                rows={data.items}
                rowKey={(e) => e.id}
              />
              <Pager page={page} pageSize={PAGE_SIZE} total={data.meta.total} onPage={setPage} />
            </>
          )
        }
      </QueryState>
    </Panel>
  );
}
